import moment from "moment";
import { v4 as uuidv4 } from "uuid";
import { Lambda, observe } from "mobx";
import { DataTypes } from "generated";
import {Calendar as VanillaCalendar} from "vanilla-calendar-pro";
import Calendar, { EventObject } from "@toast-ui/calendar";
import {
  CalendarStore,
  RootStore,
  ECalendarType,
  IWeeklyState,
  IExclude,
  ITime,
  EWeek,
  IPeriod,
} from "@lib/store";
import { MSCalendarApi } from "@lib/api";
import { Basic } from "../basic";
import { UiModal } from "../common/modal/modal.js";
import { UiFloatModal } from "../common/modal";
import { UiFloatPopover } from "../common/popover";

// CSS
import { IDateEntry, IDateEntryFormat, IMonitoredDataPayload } from "@lib/types";
import { ISchedular, TreeNode } from "./types";
import {
  popupContextMenuEvent,
  popupSelectEvent,
  popupContextMenuCalendar,
  template,
  popupDuplicateEvent,
  popupEventsList,
} from "./templates";
import { ModalManager } from "./modals/modal-manager";
import { AddWeekModal } from "./modals/week/add-week-modal";
import { EditWeekModal } from "./modals/week/edit-week-modal";
import { EditEventWeekModal } from "./modals/week/edit-event-week-modal";
import { popupEvent } from "./modals/modal.templates";
import { AddExceptionModal } from "./modals/exception/add-exception-modal";
import { EditEventExceptionModal } from "./modals/exception/edit-event-exception-modal";
import {
  formatDateEntry,
  formatWeekDayEntry,
  getDaysInDateRange,
  getDaysInDate,
  getWeekAndDayDaysEntry,
  formatEventValue,
} from "./format-shedule.helpers";
import { EditExceptionModal } from "./modals/exception/edit-exception-modal";
import { IEvent } from "@lib/store/src/calendar";
import { FwCheckboxElement } from "./modals/week/week.types";
import i18next from "i18next";
import { GlobalParameters } from "generated";

enum EView {
  day = "day",
  week = "week",
  month = "month",
}
const view = {
  today: "day",
  day: "day",
  week: "week",
  workweek: "week",
  month: "month",
};

enum ETypeEvent {
  schedule = "Недельное расписание",
  exception = "Исключение",
}

//todo
const numberTypeBacnet = [2, 3, 4, 5];

const DOUBLE_CLICK_DELAY_MS = 250;

// web component
export class MSScheduleBACNet extends Basic { 
  static get observedAttributes() {
    return super.observedAttributes.concat(["process", "objectref", "isglobal"]);
  }

  id: string = "";
  calendarSm: VanillaCalendar;
  calendarLg: Calendar;
  disposer: Lambda;
  store: CalendarStore;
  modalElement: UiFloatModal;
  weekModal: AddWeekModal;
  mainTimeElement: HTMLDivElement;
  popoverSelectDate: UiFloatPopover;
  popoverSelect: {
    popover: UiFloatPopover;
    selectElementId: string;
  };
  monitoredItemId: Array<IMonitoredDataPayload> = [];
  weekDays = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];
  perevDateBtn: Element;
  nextDateBtn: Element;
  modalManager: ModalManager;
  enumRef: Record<number, string> = {};
  calendarsEl: HTMLSelectElement;
  subscribePublishData: NodeJS.Timeout;
  private eventClickTimeout: NodeJS.Timeout | null = null;
  private watch: NodeJS.Timeout | null = null;
  private lastClickedEventInfo: {id: string; time: Date} | null = null;
  _isglobal: boolean = false;
  _objectref: string;
  api: MSCalendarApi;

  constructor() {
    super();
    this.checkComponent("ui-modal", UiModal);
    const shadow = this.attachShadow({ mode: "open" });
    shadow.innerHTML = template();
    this.api = new MSCalendarApi();
    this.modalManager = new ModalManager();
    this.clientHandle = 1; //нумератор для clientHandle-ов
    this.ackSequenceNumber = 0;
    this.Observer = {}
    this.initMomentLocale();
    this.generateStore();
    this.onStoreChange();
    this.initVars();
    this.initViewHandler();
    this.initModals();
    this.getElements();
    this.addWeek();
    this.addExceptionWeek();
    this.initControllDate();
  }

  controlDate(e: "prev" | "next") {
    let date = this.store.date.clone();
    switch (view[this.store.mode]) {
      case EView.month: {
        e === "prev" ? date.subtract(1, "month") : date.add(1, "month");
        break;
      }
      case EView.week: {
        e === "prev" ? date.subtract(7, "day") : date.add(7, "day");
        break;
      }
      default: {
        e === "prev" ? date.subtract(1, "day") : date.add(1, "day");
        break;
      }
    }
    this.store.onChange("date", date);
  }

  initControllDate() {
    this.perevDateBtn = this.shadowRoot.querySelector(".arrow.left");
    this.nextDateBtn = this.shadowRoot.querySelector(".arrow.right");
    this.perevDateBtn.addEventListener("click", (e) => {
      this.controlDate("prev");
    });
    this.nextDateBtn.addEventListener("click", (e) => {
      this.controlDate("next");
    });
  }

  // Глобальная установка локали для локализации moment.js
  initMomentLocale() {
    moment.locale(GlobalParameters.globals.Language ?? "ru");
  }

  initVars() {
    this.calendarsEl = this.shadowRoot.querySelector("#calendars-block");
    this.calendarsEl.addEventListener("change", (e: any) => {
      this.store.onChange("calendarID", e.target.value);
    });
  }

  getElements() {
    this.mainTimeElement = this.shadowRoot.getElementById(
      "time-header"
    ) as HTMLDivElement;
  }

  onStoreChange() {
    this.disposer = observe(this.store, (change) => {
      if (change.type !== "update") return;

      if (change.name === "date") return this.updateDate();
      if (
        change.name === "weeklySchedule" ||
        change.name === "excludeSchedule"
      ) {
        return this.generateSchedule();
      }

      if (change.name === "mode") return this.updateMode();
      if (change.name === "calendarID") {
        // @ts-ignore
        this.calendarsEl.value = change.newValue;
        this.getNewDataItems();
      }
      if (change.name === "calendars") { 
        this.updateCalendarsSelect(change.newValue || []) 
      }
    });
  }

  updateCalendarsSelect(data: Array<ISchedular>) {
    // Фильтрация доступных календарей
    let calendars: Array<ISchedular> = [];

//    if (this.isglobal === true) {
      calendars = data;
    //} else if (this.isglobal === false && this.objectref !== "") {
    //  const filteredIds = this.findMatchingIds(
    //    window.$pr.vmInfo.projectTree, 
    //    this.objectref, 
    //    data?.map(x => +x.id)
    //  );
    //  calendars = data?.filter(x => filteredIds?.includes(+x.id))
    //}

    this.calendarsEl.innerHTML = '';

    if (calendars.length > 0) {
      calendars.forEach((v) => {
        const selectOption = document.createElement("option") as HTMLOptionElement;
        selectOption.textContent = v.name;
        selectOption.value = String(v.id);
        this.calendarsEl.appendChild(selectOption);
      });
    } else {
      const selectOption = document.createElement("option") as HTMLOptionElement;
      selectOption.textContent = "Значения не найдены";
      selectOption.disabled = true;
      this.calendarsEl.appendChild(selectOption);
    } 
  }

  //findMatchingIds(rootNodes: TreeNode[], path: string, ids: number[]): number[] {
  //  const pathParts = path.split('.');
    
  //  // Находим целевой объект по пути
  //  let currentNode: TreeNode | undefined = undefined;
  //  let currentNodes = rootNodes;
    
  //  for (const part of pathParts) {
  //      currentNode = currentNodes.find(node => node.Name === part || node.TranslitedName === part);
  //      if (!currentNode || !currentNode.Childs) return []; // Путь не найден
  //      currentNodes = currentNode.Childs;
  //  }
    
  //  if (!currentNode) return [];
    
  //  // Собираем ID из поддерева целевого объекта
  //  const allIdsInSubtree: number[] = [];
  //  const collectIds = (node: TreeNode) => {
  //      allIdsInSubtree.push(node.ID);
  //      if (node.Childs) {
  //          node.Childs.forEach(collectIds);
  //      }
  //  };
    
  //  if (currentNode.Childs) {
  //      currentNode.Childs.forEach(collectIds);
  //  }
    
  //  // Возвращаем ID, которые есть в поддереве
  //  return ids.filter(id => allIdsInSubtree.includes(id));
  //}

  updateDate() {
    if (this.store.mode === ECalendarType.today) {
      const isToday = this.store.date.isSame(moment(), "day");
      if (!isToday) this.store.onChange("mode", ECalendarType.day);
    }
    this.updateHeaderTime();
    this.generateSchedule();
    this.calendarLg.setDate(this.store.date.toString());
  }

  updateMode() {
    const mode = this.store.mode;
    if (mode === ECalendarType.today) {
      this.store.onChange("date", moment());
    }
    if (mode === ECalendarType.week) {
      this.calendarLg.setOptions({
        week: { workweek: false },
      });
    }
    if (mode === ECalendarType.workweek) {
      this.calendarLg.setOptions({
        week: { workweek: true },
      });
    }

    const listView = this.shadowRoot.querySelectorAll(
      "#calendar-view-container li"
    );
    listView.forEach((el) => {
      el.classList.remove("active");
    });

    const targetItem = this.shadowRoot.querySelector(
      `#calendar-view-container li[view="${mode}"]`
    );

    if (targetItem) {
      targetItem.classList.add("active");
    }

    this.calendarLg.changeView(view[mode]);
    this.updateHeaderTime();
  }

  StartWatch(){
    this.watch = setInterval(() => {
      this.PublishData()
    }, 500)
  }
  async PublishData(){
    const data = await this.api.PublishData(this.ackSequenceNumber);
    if(data.sequenceNumber) this.ackSequenceNumber = data.sequenceNumber
    if (!data.recs) return; 
    if (data?.recs[0]) {
      const item = this.Observer[data.recs[0].clientHandle]
      if(item?.value && item.value.Value === data?.recs[0]?.value?.Value){
        
      } else {
        item.value = data?.recs[0].value
        const parsedSchedule = this.weeklyScheduleParser(
          data?.recs[0]?.value?.Value
        );
        this.store.onChange("weeklySchedule", parsedSchedule);
      }
    }

    if (data?.recs[1]) {
      const item = this.Observer[data.recs[1].clientHandle]
      if(item?.value && item.value.Value === data?.recs[1]?.value?.Value){

      } else {
        item.value = data?.recs[1].value
        const parsedSchedule = this.exceptionScheduleParser(
          data?.recs[1]?.value?.Value
        );
        this.store.onChange("excludeSchedule", parsedSchedule);
      }
    };
  }

  async getNewDataItems() { 
    const items = []; 
    
    if (this.monitoredItemId?.length > 0) {
      await this.api.DeleteMonitoredDataItems(this.monitoredItemId);
      this.monitoredItemId = [];
    }

    if (this.store.selectedCalendar?.EnumTypeRef) {
      const enumRef = DataTypes?.dataTypes[this.store.selectedCalendar?.EnumTypeRef];
      if (enumRef) {
        this.enumRef = {};
        
        for (let i = 0; i < enumRef.DisplayValues.length; i++) {
          if (enumRef.Indexes[i] !== undefined && enumRef.DisplayValues[i] !== undefined) {
            this.enumRef[enumRef.Indexes[i]] = enumRef.DisplayValues[i];
          }
        }
      }
    }

    if (this.store.selectedCalendar?.WeeklyScheduleInput) {
      const { itemId, path, taskId } =
        this.store.selectedCalendar.WeeklyScheduleInput;
      items.push({
        clientHandle: this.clientHandle++,
        itemId,
        taskId,
        path,
      });
    }

    if (this.store.selectedCalendar?.ExceptionInput) {
        const { itemId, path, taskId } = this.store.selectedCalendar.ExceptionInput;

      items.push({
        clientHandle: this.clientHandle++,
        itemId,
        taskId,
        path,
      });
    }
    const monitoredData = await this.api.CraeteMonitoredDataItems(items);
    if (monitoredData?.items?.length > 0) {
        this.monitoredItemId = monitoredData?.items?.filter(x => x.monitoredItemId != null).map(x => x.monitoredItemId) ?? [];
      
      monitoredData?.items?.forEach(item => {
        this.Observer[item.clientHandle] = {}
      })
    }
  }

  updateHeaderTime() {
    const date = this.store.date.clone();
    switch (this.store.mode) {
      case ECalendarType.month:
        this.mainTimeElement.innerHTML = date.format("MMMM YYYY");
        break;
      case ECalendarType.week: {
        const monday = date.clone().startOf("isoWeek").format("l");
        const sunday = date.clone().endOf("isoWeek").format("l");
        this.mainTimeElement.innerHTML = `${monday} - ${sunday}`;
        break;
      }
      case ECalendarType.workweek: {
        const monday = date.clone().startOf("isoWeek").format("l");
        const friday = date.clone().isoWeekday(5).format("l");
        this.mainTimeElement.innerHTML = `${monday} - ${friday}`;
        break;
      }
      default: {
        this.mainTimeElement.innerHTML = date.format("l");
        break;
      }
    }
  }

  initModals() {
    // Регистрируем модальные окна
    this.modalManager.addModal(
      "addScheduleWeekModal",
      new AddWeekModal(this.id, this),
      i18next.t($ => $.scheduleBacnet.createWeeklySchedule)
    );

    this.modalManager.addModal(
      "editScheduleWeekModal",
      new EditWeekModal(this.id, this),
      i18next.t($ => $.scheduleBacnet.editWeeklySchedule)
    );

    this.modalManager.addModal(
      "editEventScheduleWeekModal",
      new EditEventWeekModal(this.id, this),
      i18next.t($ => $.scheduleBacnet.editEvent)
    );

    this.modalManager.addModal(
      "addExceptionModal",
      new AddExceptionModal(this.id, this),
      i18next.t($ => $.scheduleBacnet.createException)
    );

    this.modalManager.addModal(
      "editEventExceptionModal",
      new EditEventExceptionModal(this.id, this),
      i18next.t($ => $.scheduleBacnet.editException)
    );

    this.modalManager.addModal(
      "editExceptionModal",
      new EditExceptionModal(this.id, this),
      i18next.t($ => $.scheduleBacnet.editExceptions)
    )
  }

  addWeek() {
    this.shadowRoot
      .querySelector("#open-week")
      ?.addEventListener("fwClick", () => {
        this.modalManager.openModal("addScheduleWeekModal"); // Открываем через менеджер
      });

    this.shadowRoot
      .querySelector("#edit-week")
      ?.addEventListener("fwClick", () => {
        this.modalManager.openModal("editScheduleWeekModal");
      });
  }

  addExceptionWeek() {
    this.shadowRoot
      .querySelector("#open-exception")
      .addEventListener("fwClick", () => {
      this.modalManager.openModal("addExceptionModal");
    });

    this.shadowRoot
      .querySelector("#edit-exception")
      ?.addEventListener("fwClick", () => {
        this.modalManager.openModal("editExceptionModal");
      });
  }

  /**
   * Сохранение расписания
   */
  async saveSchedule() {
    if (this.store.selectedCalendar?.WeeklyScheduleOutput) {  
        const { itemId, path, taskId } = this.store.selectedCalendar.WeeklyScheduleOutput;
      const value = this.weeklyScheduleParserToString() ?? ";;;;;;;";

      await this.api.WriteData([{
        taskId, 
        itemId, 
        path,
        operation: "move",
        value
      }]); 
    }
  }

  /**
   * Сохранение исключений
   */
  async saveException() {
    if (this.store.selectedCalendar?.ExceptionOutput) {  
        const { itemId, path, taskId } = this.store.selectedCalendar.ExceptionOutput;
      const value = this.exceptionScheduleParserToString(this.store.excludeSchedule) ?? "0,*****1900,1,00000000,0,0,0;";
      
      await this.api.WriteData([{
        taskId, 
        itemId, 
        path,
        operation: "move",
        value
      }]); 
    }
  }

  generateStore() {
    try {
      const { id, state } = RootStore.calendars.add();
      this.id = id;
      this.store = state;
    } catch (e) {
      console.log("Error in <generateStore> = ", e);
    }
  }

  initViewHandler() {
    const container = this.shadowRoot.getElementById("calendar-view-container");
    if (!container) return;
    container.addEventListener("click", (e) => {
      // @ts-ignore
      const tag = e.target.tagName;
      if (tag !== "LI") return;
      // @ts-ignore
      const view = e.target.getAttribute("view");
      this.store.onChange("mode", view);
    });
  }

  initCalendarSm() {
    try {
      this.calendarSm = new VanillaCalendar(
        this.shadowRoot.querySelector("#calendar-sm") as HTMLDivElement,
        {
          onClickDate: (self, e) => {
            // @ts-ignore
            const date = self.context.selectedDates.at(0);
            if (date != null) {
              this.store.onChange("date", moment(new Date(date)));
            }
          },
          locale: {
            months: {
              long: [
                  i18next.t($ => $.scheduleBacnet.january),
                  i18next.t($ => $.scheduleBacnet.february),
                  i18next.t($ => $.scheduleBacnet.march),
                  i18next.t($ => $.scheduleBacnet.april),
                  i18next.t($ => $.scheduleBacnet.may),
                  i18next.t($ => $.scheduleBacnet.june),
                  i18next.t($ => $.scheduleBacnet.july),
                  i18next.t($ => $.scheduleBacnet.august),
                  i18next.t($ => $.scheduleBacnet.september),
                  i18next.t($ => $.scheduleBacnet.october),
                  i18next.t($ => $.scheduleBacnet.november),
                  i18next.t($ => $.scheduleBacnet.december),
              ],
              short: [
                  i18next.t($ => $.scheduleBacnet.january),
                  i18next.t($ => $.scheduleBacnet.february),
                  i18next.t($ => $.scheduleBacnet.march),
                  i18next.t($ => $.scheduleBacnet.april),
                  i18next.t($ => $.scheduleBacnet.may),
                  i18next.t($ => $.scheduleBacnet.june),
                  i18next.t($ => $.scheduleBacnet.july),
                  i18next.t($ => $.scheduleBacnet.august),
                  i18next.t($ => $.scheduleBacnet.september),
                  i18next.t($ => $.scheduleBacnet.october),
                  i18next.t($ => $.scheduleBacnet.november),
                  i18next.t($ => $.scheduleBacnet.december),
              ],
          },
          weekdays: {
              long: [
                  i18next.t($ => $.scheduleBacnet.su),
                  i18next.t($ => $.scheduleBacnet.mo),
                  i18next.t($ => $.scheduleBacnet.tu),
                  i18next.t($ => $.scheduleBacnet.we),
                  i18next.t($ => $.scheduleBacnet.th),
                  i18next.t($ => $.scheduleBacnet.fr),
                  i18next.t($ => $.scheduleBacnet.sa),
              ],
              short: [
                  i18next.t($ => $.scheduleBacnet.su),
                  i18next.t($ => $.scheduleBacnet.mo),
                  i18next.t($ => $.scheduleBacnet.tu),
                  i18next.t($ => $.scheduleBacnet.we),
                  i18next.t($ => $.scheduleBacnet.th),
                  i18next.t($ => $.scheduleBacnet.fr),
                  i18next.t($ => $.scheduleBacnet.sa),
              ],
            }
          },
          enableJumpToSelectedDate: true,
        }
      );
      this.calendarSm.init();
    } catch (e) {
      console.log("Error in <initCalendarSm> ", e);
    }
  }

  initCalendarLg() {
    try {
      this.calendarLg = new Calendar(
        this.shadowRoot.querySelector("#calendar-lg") as HTMLDivElement,
        {
          useFormPopup: false,
          useDetailPopup: false,
          isReadOnly: true,
          gridSelection: {
            enableClick: true,
            enableDblClick: true,
          },
          week: {
            eventView: ["time"],
            taskView: false,
            startDayOfWeek: 1,
            dayNames: [
              i18next.t($ => $.scheduleBacnet.su),
              i18next.t($ => $.scheduleBacnet.mo),
              i18next.t($ => $.scheduleBacnet.tu),
              i18next.t($ => $.scheduleBacnet.we),
              i18next.t($ => $.scheduleBacnet.th),
              i18next.t($ => $.scheduleBacnet.fr),
              i18next.t($ => $.scheduleBacnet.sa),
            ],
            workweek: true,
          },
          month: {
            startDayOfWeek: 1,
            dayNames: [
              i18next.t($ => $.scheduleBacnet.su),
              i18next.t($ => $.scheduleBacnet.mo),
              i18next.t($ => $.scheduleBacnet.tu),
              i18next.t($ => $.scheduleBacnet.we),
              i18next.t($ => $.scheduleBacnet.th),
              i18next.t($ => $.scheduleBacnet.fr),
              i18next.t($ => $.scheduleBacnet.sa),
            ],
          },
          defaultView: "week",
          template: {
            time(event) {
              return `<span class="calendar-time">${moment(
                event.start.d.d
              ).format("HH:mm")}, ${event.body}</span>`;
            },
            weekGridFooterExceed(hiddenEvents) {
              return `+${hiddenEvents}`;
            },
            timegridDisplayPrimaryTime({ time }) {
              return `${moment(time.d.d).format("HH:mm")}`;
            },
            monthDayName(model) {
              const isHoliday = model.day === 0 || model.day === 6;
              return `<span class=${isHoliday ? 'day-weekend' : 'day-workday'}>${model.label}</span>`;
            },
            monthGridHeader: (model) => {
              const day = parseInt(model.date.split('-')[2], 10);
              const isHoliday = model.day === 0 || model.day === 6;
              return `<span
                data-date="${model.date}"
                class="${model.isToday ?
                  'toastui-calendar-weekday-grid-date toastui-calendar-weekday-grid-date-decorator toastui-calendar-template-monthGridHeader' :
                  'toastui-calendar-grid-cell-date'
                } day-name ${isHoliday ? 'day-weekend' : 'day-workday'}">
                  ${day}
                </span>`
            },
            monthGridHeaderExceed: function (hiddenEvents) {
              return (
                '<span class="calendar-month-header-exceed">' +
                  `${hiddenEvents} ${i18next.t($ => $.scheduleBacnet.more)} ` + 
                '</span>'
              );
            },
            monthMoreTitleDate(moreTitle) {
              const { ymd } = moreTitle;
              const parsedDate = moment(ymd, 'YYYY-MM-DD');

              return `
                <span class="toastui-calendar-more-title-date">${parsedDate.format('DD')}</span>
                <span class="toastui-calendar-more-title-day">${parsedDate.format('ddd')}</span>
                `;
            },
            weekDayName: (model) => {
              const isHoliday = model.day === 0 || model.day === 6;

              return `
                <span class=${isHoliday ? 'day-weekend' : 'day-workday'}>
                  <span class="toastui-calendar-day-name__date">${model.date}</span>
                  &nbsp;&nbsp;
                  <span class="toastui-calendar-day-name__name">${model.dayName}</span>
                </span>
              `;
            },   
          },
        }
      );

       this.calendarLg.on("clickMoreEventsBtn", ({date, target}) => {
        const eventsIdData = Array.from(target.querySelectorAll('.toastui-calendar-weekday-event-block')).map(element => ({
          eventId: element.getAttribute("data-event-id"),
          calendarId: element.getAttribute("data-calendar-id"),
        }));
        const events: EventObject[] = [];
        for (const eventIds of eventsIdData) {
          const event = this.calendarLg.getEvent(eventIds.eventId, eventIds.calendarId);
          if (event !== null) {
            events.push(event);
          }
        }

        const header = this.shadowRoot?.querySelector("#calendar-lg")?.querySelector(`.day-name[data-date='${moment(date).format("YYYY-MM-DD")}']`)?.parentElement?.parentElement?.parentElement;
        const button = header?.querySelector(".toastui-calendar-grid-cell-more-events");
        const rect = button?.getBoundingClientRect();

        if (rect == null) {
          return;
        }

        this.showEventsPopup(
          {
            x: rect.x,
            y: rect.y,
          },
          events,
          date,
          () => {
            this.closeCalendarLgMoreEventsPopup();
          }
        )
      })

      this.calendarLg.on("clickDayName", (info) => {
        const { date } = info;
        this.calendarSm.set({
          selectedDates: [date],
        })
        this.store.onChange("date", moment(new Date(date)));
      })

      // Обработчик перетаскивания определенной даты или времени
      this.calendarLg.on("selectDateTime", (event) => {
        const x = event.nativeEvent.clientX;
        const y = event.nativeEvent.clientY;
        const popover = new UiFloatPopover(
          popupContextMenuCalendar(),
          {
            x,
            y,
          }
        );
      });

      // Обработчик событий (ЛКМ) по нажатию на события в календаре
      this.calendarLg.on("clickEvent", (event) => {

        if ([ECalendarType.today, ECalendarType.day, ECalendarType.week, ECalendarType.workweek].includes(this.store.mode)) {
          const events = this.getNextEvents(event.event);
          const threshold = [ECalendarType.today, ECalendarType.day].includes(this.store.mode) ? 6 : 4;
          if (events.length >= threshold) {
            this.showEventsPopup({
                x: event.nativeEvent.clientX,
                y: event.nativeEvent.clientY,
              },
              events,
              event.event.start.d.d
            );
            return;
          }
        }

        this.handleEventClick(event.nativeEvent, event.event);
      });

      // Обработчик событий (ПКМ) по нажатию на события в календаре
      this.shadowRoot
        .querySelector("#calendar-lg")
        .addEventListener("contextmenu", (e: MouseEvent) => {
          // Проверяем на клик в зоне события календаря
          const { calendarId, eventId } = this.getEventData(
            e.target as Element
          );
          const event = this.calendarLg.getEvent(eventId, calendarId);
          if (event) {
            if ([ECalendarType.today, ECalendarType.day, ECalendarType.week, ECalendarType.workweek].includes(this.store.mode)) {
              const events = this.getNextEvents(event);
              const threshold = [ECalendarType.today, ECalendarType.day].includes(this.store.mode) ? 6 : 4;
              if (events.length >= threshold) {
                const calendarStart = moment(this.calendarLg.getDateRangeStart().toDate());
                const date = moment(event.start.toDate());
                while (date.isBefore(calendarStart, 'days')) {
                  date.add(7, 'days');
                }
                this.showEventsPopup({
                    x: e.clientX,
                    y: e.clientY,
                  }, 
                  events, 
                  date.toDate()
                );
                return;
              }
            }

            this.handleEventRightClick(e, event);
          } else {
            this.handleCalendarRightClick(e);
          }
        });

      (this.shadowRoot.querySelector("#calendar-lg") as HTMLDivElement).addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        if (target.classList.contains('toastui-calendar-grid-cell-date') || target.classList.contains('toastui-calendar-weekday-grid-date')) {
          const date = target.getAttribute('data-date');
          this.calendarSm.set({
            selectedDates: [date],
          })
          this.store.onChange("date", moment(new Date(date)));
        }
      })

      this.updateDate();
      this.updateMode();
    } catch (e) {
      console.log("Error in <initCalendarLg> ", e);
    }
  }

  handleEventClick(event: MouseEvent, eventObject: EventObject, parent?: HTMLElement, afterModalOpen?: () => void): void {
    event.stopPropagation();
    event.preventDefault();
    const x = event.clientX;
    const y = event.clientY;

    const startTime = moment(eventObject.start.d.d).format(
      "dddd, D MMMM HH:mm"
    );
    const body = `${startTime}, ${eventObject.body}`;

    const eventId = eventObject.id;

    const clickTime = new Date();

    // Проверяем на двойной клик
    if (
      this.lastClickedEventInfo !== null &&
      eventId === this.lastClickedEventInfo.id &&
      clickTime.getTime() - this.lastClickedEventInfo.time.getTime() < DOUBLE_CLICK_DELAY_MS
    ) {
      clearTimeout(this.eventClickTimeout)
      this.eventClickTimeout = null;
      this.lastClickedEventInfo = null;

      if (eventObject.title === ETypeEvent.exception) {
        this.modalManager.openModal("editEventExceptionModal", {
          event: eventObject,
        });
      } else {
        this.modalManager.openModal("editEventScheduleWeekModal", {
          event: eventObject,
        });
      }
      afterModalOpen?.();

      return;
    }

    this.lastClickedEventInfo = {
      id: eventId,
      time: clickTime,
    }

    this.eventClickTimeout = setTimeout(() => {
      this.eventClickTimeout = null;
      this.lastClickedEventInfo = null;

      const popover = new UiFloatPopover(
        popupSelectEvent(body),
        {
          x,
          y,
        },
        undefined,
        undefined,
        undefined,
        parent,
      );

      // Установка цвета границы для квадратика
      popover.root.style.setProperty(
        "--square-color",
        eventObject.borderColor
      );

      popover.root
        .querySelector("#close-icon")
        .addEventListener("click", () => popover.destroy());

      popover.root.querySelector("#edit").addEventListener("click", () => {
        if (eventObject.title === ETypeEvent.exception) {
          this.modalManager.openModal("editEventExceptionModal", {
            event: eventObject,
          });
        } else {
          this.modalManager.openModal("editEventScheduleWeekModal", {
            event: eventObject,
          });
        }

        afterModalOpen?.();
        popover.destroy();
      });

      popover.root
        .querySelector("#delete")
        .addEventListener("click", () => {
          popover.hideOnOuterClick = false;
          this.showDeleteConfirmationPopup(
            event,
            () => {
              this.deleteEventById(eventObject);
              popover.destroy();
            },
            () => {
              setTimeout(() => {
                popover.hideOnOuterClick = true;
              }, 0);
            },
            popover.root,
            parent,
          );
        });
    }, DOUBLE_CLICK_DELAY_MS);
  }

  handleEventRightClick(e: MouseEvent, event: EventObject, parent?: HTMLElement, afterModalOpen?: () => void): void {
    e.stopPropagation();
    e.preventDefault();
    const x = e.clientX;
    const y = e.clientY;

    if (event) {
      setTimeout(() => {
        const popover = new UiFloatPopover(
          popupContextMenuEvent(event.title === ETypeEvent.exception),
          {
            x,
            y,
          },
          undefined,
          undefined,
          undefined,
          parent,
        );
 
        popover.root
          .querySelector("#edit")
          .addEventListener("click", () => {
            if (event?.title === ETypeEvent.exception) {
              this.modalManager.openModal("editEventExceptionModal", {
                event,
              });
            } else {
              this.modalManager.openModal("editEventScheduleWeekModal", {
                event,
              });
            }

            afterModalOpen?.();
            popover.destroy();
          });

        popover.root
          .querySelector("#delete")
          .addEventListener("click", () => {
            this.showDeleteConfirmationPopup(e, () => {
              this.deleteEventById(event);
              popover.destroy();
            }, undefined, undefined, parent);
          });

        this.showDuplicateEventPopup(
          popover,
          event,
          popover.root,
        );
      }, 0);
    }
  }

  handleCalendarRightClick(e: MouseEvent): void {
    e.stopPropagation();
    e.preventDefault();
    const x = e.clientX;
    const y = e.clientY;

    setTimeout(() => {
      const popover = new UiFloatPopover(
        popupContextMenuCalendar(),
        {
          x,
          y,
        }
      );

      popover.root
        .querySelector("#add")
        .addEventListener("click", () => {
          this.modalManager.openModal("addScheduleWeekModal");
          popover.destroy();
        });

      popover.root
        .querySelector("#exclude")
        .addEventListener("click", () => {
          this.modalManager.openModal("addExceptionModal");
          popover.destroy();
        });
    }, 0);
  }

  getNextEvents(targetEvent: EventObject): EventObject[] {
    const result: EventObject[] = [targetEvent];
    const targetStart = moment(targetEvent.start.d.d);
    const targetEnd = moment(targetEvent.end.d.d);

    const numberWeekDay = targetStart.isoWeekday();

    const weeklySchedules = this.store.weeklySchedule[numberWeekDay - 1 as EWeek];
    const excludeSchedule = this.store.excludeSchedule

    const isNext = (schedule: IEvent | IExclude): boolean => {
      const { hours, minutes, seconds } = schedule.time;
      const eventStart = targetStart.clone().hour(parseInt(hours)).minute(parseInt(minutes)).second(parseInt(seconds));
      const eventEnd = eventStart.clone().add(10, 'minutes');

      const startDifference = targetStart.diff(eventStart, 'minutes')
      const endDifference = targetEnd.diff(eventEnd, 'minutes')

      return startDifference <= 25 && startDifference >= 0 || endDifference >=-25 && endDifference <= 0;
    }

    for (const schedule of [...weeklySchedules, ...excludeSchedule]) {
      if (schedule.id === targetEvent.id) {
        continue;
      }
      if (isNext(schedule)) {
        const event = this.calendarLg.getEvent(schedule.id, targetEvent.calendarId);
        if (event) {
          result.push(event);
        }
      }
    }

    return result;
  }

  private closeCalendarLgMoreEventsPopup(): void {
    const overlay = this.shadowRoot?.querySelector<HTMLDivElement>('.toastui-calendar-popup-overlay');
    if (overlay != null) {
      overlay.click();
    }
  }

  showEventsPopup(position: {x: number, y: number}, events: EventObject[], date: Date, afterModalOpen?: () => void): void {
    setTimeout(() => {
      const popover = new UiFloatPopover(
        popupEventsList(date),
        {
          x: position.x,
          y: position.y,
        },
        undefined,
        undefined,
        () => {
          this.closeCalendarLgMoreEventsPopup();
        },
        undefined,
        {
          initialMaxHeight: '186px',
        },
        true,
      );

      const listElement = popover.root.querySelector('.events-list');

      [...events]
        .sort((event1, event2) => moment(event1.start.d.d).format('hh:mm') > moment(event2.start.d.d).format('hh:mm') ? 1 : -1)
        .forEach(event => {
          const itemElement = document.createElement('li');

          itemElement.innerHTML = `${moment(event.start.d.d).format('HH:mm')}, ${event.body}`
          itemElement.classList.add(
            'item',
            event.title === ETypeEvent.exception ? 'item_exception' : 'item_weekly',
          );

          itemElement.addEventListener('click', (e) => {
            this.handleEventClick(e, event, popover.root, () => {
              popover.destroy()
              afterModalOpen?.();
            });
          })
          itemElement.addEventListener('contextmenu', (e) => {
            this.handleEventRightClick(e, event, popover.root, () => {
              popover.destroy();
              afterModalOpen?.();
            });
          })

          listElement.appendChild(itemElement);
        });
    }, 0);
  }

  showDuplicateEventPopup(
    popover: UiFloatPopover,
    calendarEvent: EventObject,
    parent: HTMLElement,
  ) {
    const editButton = popover.root.querySelector<HTMLElement>("#edit");
    const deleteButton = popover.root.querySelector<HTMLElement>("#delete");
    const duplicateButton = popover.root.querySelector<HTMLElement>("#duplicate");

    if (duplicateButton == null) {
      return;
    }

    let duplicatePopover: UiFloatPopover | null = null;

    const duplicateEvent = () => {
      const copyWeeklySchedule = JSON.parse(
        JSON.stringify(this.store.weeklySchedule)
      ) as IWeeklyState;

      if (duplicatePopover == null) {
        return;
      }

      let event: IEvent;

      if (calendarEvent?.id) {
        for (let i = 0; i <= 6; i++) {
          const items = copyWeeklySchedule[i as EWeek];
          if (!items) continue;

          const targetEvent = items.find(item => item.id === calendarEvent.id);
          if (targetEvent != null) {
            event = targetEvent;
            break;
          }
        }

        const checkboxes = Array.from(duplicatePopover.root.querySelectorAll<FwCheckboxElement>("fw-checkbox"));
        const selectedDays = checkboxes.map<EWeek>((el, index) => (el.checked ? index : null)).filter((el) => el !== null);
        if (event != null) {
          for (const weekday of selectedDays) {
            copyWeeklySchedule[weekday].push({
              ...event,
              id: uuidv4(),
            })
          }
          this.store.onChange("weeklySchedule", copyWeeklySchedule);
          localStorage.setItem(
            "weeklySchedule",
            this.weeklyScheduleParserToString()
          );
          this.saveSchedule();
        }
      }
    }

    let isOverDuplicatePopover = false;
    let isOverButton = false;

    const hideDuplicatePopover = () => {
      if (!isOverDuplicatePopover && !isOverButton) {
        if (duplicatePopover) {
          duplicatePopover.onDestroy = undefined;
          duplicatePopover.destroy();
        }
        duplicatePopover = null;
      } 
    }

    const openDuplicatePopover = () => setTimeout(() => {
      if (duplicatePopover !== null) {
        return;
      }

      duplicatePopover = new UiFloatPopover(
        popupDuplicateEvent(),
        popover.root,
        ["right-start", "right", "right-end"],
        true,
        () => {
          duplicateEvent();
          duplicatePopover = null;
        },
        popover.root,
        undefined,
        undefined,
        true,
      );

      duplicatePopover.root.addEventListener("mouseenter", () => {
        isOverDuplicatePopover = true;
      })

      duplicatePopover.root.addEventListener("mouseleave", () => {
        isOverDuplicatePopover = false;
      })
    }, 0)
    

    duplicateButton.addEventListener("mouseenter", () => {
      openDuplicatePopover();
      isOverButton = true;
    })

    duplicateButton.addEventListener("mouseleave", () => {
      isOverButton = false;
      setTimeout(hideDuplicatePopover, 100);
    })

    editButton?.addEventListener("mouseenter", () => {
      isOverDuplicatePopover = false;
      isOverButton = false;
      setTimeout(hideDuplicatePopover, 100);
    })

    deleteButton?.addEventListener("mouseenter", () => {
      isOverDuplicatePopover = false;
      isOverButton = false;
      setTimeout(hideDuplicatePopover, 100);
    })
  }

  /**
   * Показ попапа подтверждения удаления
   */
  showDeleteConfirmationPopup(e: MouseEvent, onConfirm: () => void, onCancel?: () => void, anchor?: HTMLElement, parent?: HTMLElement) {
    setTimeout(() => {
      const popover = new UiFloatPopover(
        popupEvent({
          title: i18next.t($ => $.scheduleBacnet.deletion),
          desc: i18next.t($ => $.scheduleBacnet.eventDeleteConfirmation),
        }),
        anchor ?? {
          x: e.clientX,
          y: e.clientY,
        },
        undefined,
        true,
        () => {
          if (onCancel) {
            onCancel();
          }
        },
        parent,
      );

      popover.root.querySelector("#primary")?.addEventListener("click", () => {
        if (onConfirm) {
          onConfirm();
        }
        popover.destroy();
      });

      popover.root
        .querySelector("#secondary")
        ?.addEventListener("click", () => {
          popover.destroy();
        });
    }, 0);
  }

  /** Извлечение id события и календаря из родительского элемента
   *  @param {Element} target DOM-element для извлечения данных
   *  @return {{ calendarId, eventId }} данные события
   * */
  getEventData(target: Element) {
    let eventElement: Element;

    // Выбор события на дневном/недельном тайм-фрейме
    if (target.closest(".toastui-calendar-event-time")) {
      eventElement = target.closest(".toastui-calendar-event-time");
    }
    // Выбор события на месячном тайм-фрейме (в списке детализации событий)

    if (target.closest(".toastui-calendar-weekday-event-block")) {
      eventElement = target.closest(".toastui-calendar-weekday-event-block");
    }

    // Обработка клика по событию на дневном/недельном тамй-фрейме
    if (eventElement) {
      return {
        calendarId: eventElement.getAttribute("data-calendar-id"),
        eventId: eventElement.getAttribute("data-event-id"),
      };
    }

    return { calendarId: null, eventId: null };
  }

  /** Удаление событие по eventId
   *  @param {string} eventId ID события
   *  @return {void}
   * */
  deleteEventById(event: any): void { 
    if (event?.title === ETypeEvent.exception) {
      const excludeEvents: Array<IExclude> = JSON.parse(
        JSON.stringify(this.store.excludeSchedule)
      );
      const events = excludeEvents?.filter((x) => x.id !== event?.id) || []; 
      
      this.store.onChange("excludeSchedule", events); 

      this.saveException();
    } else {
      const copyWeeklySchedule = JSON.parse(
        JSON.stringify(this.store.weeklySchedule)
      ) as IWeeklyState;

      if (event?.id) {
        for (let i = 0; i <= 6; i++) {
          const items = copyWeeklySchedule[i as EWeek];
          if (!items) continue;

          const updatedItems = items.filter((item) => item.id !== event?.id);
          copyWeeklySchedule[i as EWeek] = updatedItems;
        }
      }

      this.store.onChange("weeklySchedule", copyWeeklySchedule);
      localStorage.setItem(
        "weeklySchedule",
        this.weeklyScheduleParserToString()
      );
      this.saveSchedule();
    }
  }

  async getInitialRequest() {
    try {
      await this.api.CraeteDataSubscription();
        this.StartWatch();

        let path = this.isglobal ? "" : this.objectref;
        let itemId = 0;
        if (!this.isglobal && !path) {
            itemId = $sw.ItemSubscription._getParentParam(this, "objectid", true);
            path = $sw.ItemSubscription._getOuterPath(this);
        }

        const calendars = await this.api.GetBacnetSchedules(itemId, path);

      this.store.onChange("calendars", calendars.schedules);
      if (calendars.schedules?.length)
        this.store.onChange("calendarID", calendars.schedules[0].id);
    } catch (e) {
      console.log("error in getInitialRequest", e);
    }
  }

  setMomentDate(
    year: number,
    month: number,
    day: number,
    hours: number,
    minutes: number,
    seconds: number
  ) {
    const momentDate = moment([year, month, day, hours, minutes, seconds]);
    return momentDate;
  }

  generateSchedule() {
    const schedule = this.generateWeek().sort((event1, event2) => event1.start > event2.start ? 1 : -1);
    const exclude = this.generateExclude().sort((event1, event2) => event1.start > event2.start ? 1 : -1);
    this.calendarLg.clear();
    this.calendarLg.createEvents(schedule.concat(exclude));
  }

  generateWeek() {
    if (!this.store.weeklySchedule && !this.store.excludeSchedule.length) return [];
    const res = [];

    const date = this.store.date.clone();
    const countDay = date.clone().daysInMonth();

    for (let i = 1; i <= countDay; i++) {
      const day = date.clone().date(i);
      const numberWeekDay = day.clone().isoWeekday();
      const weekday = this.store.weeklySchedule[numberWeekDay - 1];
      if (weekday) {
        weekday?.forEach((element) => {
          const { hours, minutes, seconds } = element.time;
          let newDate = day.clone().hour(hours).minute(minutes).second(seconds);
          let newDate2 = newDate.clone().add(10, "minutes");
          res.push({
            body: `${formatEventValue(element, this.enumRef)}`,
            calendarId: "1",
            category: "time",
            dueDateClass: "",
            end: newDate2.toString(),
            id: element.id,
            start: newDate.toString(),
            title: ETypeEvent.schedule,
            backgroundColor: "#B9C9EC",
            borderColor: "#2C6FD7",
            type: "schedule",
            customStyle: {
              backgroundColor: "#B9C9EC",
            },
          });
        });
      }
    }

    return res;
  }

  // Основной метод для генерации исключений
  generateExclude(): EventObject[] {
    if (!this.store.excludeSchedule) return [];

    const events: EventObject[] = [];

    this.store?.excludeSchedule?.forEach((exception) => {
      const period = exception.period;
      const time = exception.time;
      const value = formatEventValue(exception, this.enumRef);

      // Обработка разных типов исключений
      switch (period.type) {
        case 0: // Date Entry
          this.handleDateEntry(period, time, value, exception.id, events);
          break;
        case 1: // Date Range Entry
          this.handleDateRangeEntry(period, time, value, exception.id, events);
          break;
        case 2: // Week And Day Entry
          this.handleWeekAndDayEntry(period, time, value, exception.id, events);
          break;
        default:
          console.warn(`Unknown period type: ${period.type}`);
          break;
      }
    });

    return events;
  }

  connectedCallback() {
    super.connectedCallback();
    this.initCalendarSm();
    this.initCalendarLg();
    this.getInitialRequest();
  }

  timeParser(str: string) {
    // HH 0 - 23
    // mm 0 - 59
    // ss 0 - 59
    // hh 0 - 99

    const hours = str[0] + str[1];
    const minutes = str[2] + str[3];
    const seconds = str[4] + str[5];
    const hundredth = str[6] + str[7];

    return {
      hours,
      minutes,
      seconds,
      hundredth,
    } as ITime;
  }

  // dDDMMYYYY
  entryRangeDateParser(date: string): IDateEntryFormat {
    const obj = {
      weekDay: this.safeParseFloat(date[0]) ?? "*",
      day: this.safeParseFloat(date[1] + date[2]) ?? "*",
      month: this.safeParseFloat(date[3] + date[4]) ?? "**",
      year: this.safeParseFloat(date.slice(-4)) ?? "****",
    };

    // На бэке **** конвертируется в 2155
    if (obj.year === 2155) {
      obj.year = "****";
    }

    return obj;
  }

  dateEntryFormater(data: Array<string>): IDateEntry {
    const emptyDate = {
      weekDay: "*",
      day: "*",
      week: "*",
      month: "**",
      year: "****",
    };
    const type = parseInt(data[0]);
    if (type === 0) {
      // Date Entry Format (0,dDDMMYYYY)
      return {
        type,
        start: this.entryRangeDateParser(data[1]),
        end: emptyDate,
      };
    } else if (type === 1) {
      // Date Range Entry Format (1,dDDMMYYYY,dDDMMYYYY)
      const start = this.entryRangeDateParser(data[1]);
      const end = this.entryRangeDateParser(data[2]);
      return { type, start, end };
    } else if (type === 2) {
      // Week And Day Entry Format (2,MMWd)
      return {
        type,
        start: {
          month: this.safeParseFloat(data[1].substring(0, 2)),
          week: this.safeParseFloat(data[1].substring(2, 3)),
          weekDay: this.safeParseFloat(data[1].substring(3, 4)),
          year: emptyDate.year,
        },
        end: emptyDate,
      };
    } else {
      return {
        type,
        start: emptyDate,
        end: emptyDate,
        calRef: this.safeParseFloat(data[1]),
      };
      // type will be 3 Calendar Reference
    }
  }

  weeklyScheduleParser(str: string): IWeeklyState {
    if (!str) {
      str = ";;;;;;;";
    }

    const daysOfWeek = ["0", "1", "2", "3", "4", "5", "6"];
    const scheduleEntries = str.replace(/;$/, "").split(";");
    const scheduleObject = {} as IWeeklyState;

    scheduleEntries?.forEach((entry, i) => {
      if (!entry) {
        // @ts-ignore
        scheduleObject[daysOfWeek[i]] = [];
        return;
      }

      const parts = entry.split(",");

      const parsedData = [];

      for (let i = 0; i < parts.length; i += 4) {
        if (i + 3 < parts.length) {
          const dataType = parseInt(parts[i + 1]);
          const IsNumberType = numberTypeBacnet.some((num) => num === dataType);
          const resultObj = {
            id: uuidv4(),
            time: this.timeParser(parts[i]),
            dataType,
            length: parseInt(parts[i + 2]) || parts[i + 2],
          };
          if (dataType === 12) {
            resultObj.value = `${parts[i + 3]},${parts[i + 4]}`;
          } else if (dataType === 4 || dataType === 5) {
            // Decimal number
            resultObj.value = IsNumberType
              ? this.safeParseFloat(parts[i + 3], 6)
              : parts[i + 3];
          } else {
            resultObj.value = IsNumberType
              ? this.safeParseFloat(parts[i + 3])
              : parts[i + 3];
          }
          parsedData.push(resultObj);
        }
      }
      // @ts-ignore
      scheduleObject[daysOfWeek[i]] = parsedData;
    });
    return scheduleObject;
  }

  safeParseFloat(str: string, toFixed?: number) {
    if (/^-?\d+(\.\d+)?$/.test(str)) {
      if (toFixed) {
        return parseFloat(str).toFixed(toFixed);
      }
      return parseFloat(str);
    }
    return str;
  }

  weeklyScheduleParserToString() {
    let result = "";
    if (!this.store.weeklySchedule) return result;

    for (let i = 0; i <= 6; i++) {
      const dayOfWeek = this.store.weeklySchedule[i];

      let resultDay = "";
      dayOfWeek &&
        dayOfWeek?.forEach((task) => {
          const { hours, minutes, seconds, hundredth } = task.time;
          resultDay =
            resultDay +
            `${hours}${minutes}${seconds}${hundredth},${task.dataType},${task.length},${task.value},`;
        });
      result = result + `${resultDay.length ? resultDay.slice(0, -1) : ""};`;
    }

    return result;
  }

  /**
   * Парсинг строки исключений в json
   * @param {string} str Строка исключения
   * @returns {Array<IExclude>} JSON
   */
  exceptionScheduleParser(str: string): Array<IExclude> {
    if (!str || str === "0,*****1900,0,;") {
      str = "";
    } 
    const scheduleEntries = str.replace(/;$/, "").split(";");
     
    const parsedSchedule = scheduleEntries
      .map((entry) => {
        try {
          const values = entry.split(",");
          const type = parseInt(values[0]);

          // Смещение индекса для типа (Date Range Entry Format: 1,dDDMMYYYY,dDDMMYYYY) из-за второй даты
          const offset = type === 1 ? 1 : 0;

          // Парсим период
          const period = this.dateEntryFormater(values);

          // Парсим время
          const time = this.timeParser(values[3 + offset]);

          // Парсим приоритет
          const priority = parseInt(values[2 + offset]);

          // Парсим тип данных и значение
          const dataType = parseInt(values[4 + offset]);
          const isNumberType = numberTypeBacnet.includes(dataType);

          let value;
          // Decimal number
          if (dataType === 3 || dataType === 4) {
            value = isNumberType
            ? this.safeParseFloat(values[6 + offset], 6)
            : values[6 + offset];
          } else {
            value = isNumberType
            ? this.safeParseFloat(values[6 + offset])
            : values[6 + offset];
          }
          

          // Создаем объект исключения
          return {
            id: uuidv4(),
            period,
            priority,
            time,
            dataType,
            length: String(value).length,
            value,
          } as IExclude;
        } catch (e) {
          console.warn("Error parse exceptionSchedule: ", e);
        }
      })
      .filter(Boolean);
    return parsedSchedule;
  }

  /**
   * Парсинг json исключений в строку
   * @param {Array<IExclude>} exceptions Список исключений в JSON
   * @returns {string} String row
   */
  exceptionScheduleParserToString(exceptions: Array<IExclude>): string {
    if (!Array.isArray(exceptions) || exceptions?.length === 0) return '0,*****1900,1,00000000,0,0,0;';
    let result = "";

    exceptions?.forEach((exception) => {
      const { period, priority, time, dataType, length, value } = exception;
      const { type, start, end } = period;

      let periodStr = `${type},`;

      if (type === 0 || type === 1) {
        // Date Entry или Date Range Entry
        periodStr += formatDateEntry(start);
        if (type === 1) {
          periodStr += `,${formatDateEntry(end)}`;
        }
      } else if (type === 2) {
        // Week And Day Entry
        periodStr += formatWeekDayEntry(start);
      } else if (type === 3) {
        // Calendar Reference Entry
        periodStr += period.calRef;
      }

      const timeStr = `${time.hours}${time.minutes}${time.seconds}${time.hundredth}`;

      result += `${periodStr},${priority},${timeStr},${dataType},${length},${value};`;
    });

    return result;
  }

  // Создание событий для массива дней
  private createEventsForDays(
    days: number[],
    time: ITime,
    value: any,
    id: string,
    events: EventObject[]
  ) {
    days.forEach((day) => {
      const startDate = this.store.date
        .clone()
        .date(day)
        .hour(time.hours)
        .minute(time.minutes)
        .second(time.seconds);
      const endDate = startDate.clone().add(10, "minutes");

      events.push({
        body: `${value}`,
        calendarId: "1",
        category: "time",
        dueDateClass: "",
        end: endDate.toString(),
        id,
        start: startDate.toString(),
        title: ETypeEvent.exception,
        backgroundColor: "#7827CA4D",
        borderColor: "#7827CA",
        type: "exception",
        customStyle: {
          backgroundColor: "#7827CA4D",
        },
      });
    });
  }

  // Обработка Date Entry (тип 0)
  private handleDateEntry(
    period: IPeriod,
    time: ITime,
    value: any,
    id: string,
    events: EventObject[]
  ) {
    // Получаем дни для обработки
    const days = getDaysInDate(this.store.date.clone(), period.start);

    // Создаем события для каждого подходящего дня
    this.createEventsForDays(days, time, value, id, events);
  }

  // Обработка Date Range Entry (тип 1)
  private handleDateRangeEntry(
    period: IPeriod,
    time: ITime,
    value: any,
    id: string,
    events: EventObject[]
  ) {
    // Получаем дни для обработки
    const days = getDaysInDateRange(
      this.store.date.clone(),
      period.start,
      period.end
    );

    // Создаем события для каждого подходящего дня
    this.createEventsForDays(days, time, value, id, events);
  }

  // Обработка Week And Day Entry (тип 2)
  private handleWeekAndDayEntry(
    period: IPeriod,
    time: ITime,
    value: any,
    id: string,
    events: EventObject[]
  ) { 
    // Получаем дни для обработки
    const days = getWeekAndDayDaysEntry(this.store.date.clone(), period.start);

    // Создаем события для каждого подходящего дня
    this.createEventsForDays(days, time, value, id, events);
  }

  get isglobal() {
    return this._isglobal;
  }

  set isglobal(value: string | boolean) {
    this._isglobal = value === "true";
  }
  
  get objectref() {
    return this._objectref;
  }

  set objectref(value) {
    this._objectref = value;
  }

  disconnectedCallback() {
    this.disposer();
    super.disconnectedCallback();
    this.watch && clearInterval(this.watch);
    this.eventClickTimeout && clearTimeout(this.eventClickTimeout);
  }
}
